import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sdk } from '@/lib/sdk'
import { PLATFORM_IDS } from '@kiddocaremy/sdk-ts'
import type { User } from '@kiddocaremy/sdk-ts'

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null)
    const isInitialized = ref(false)

    const isAuthenticated = computed(() => !!user.value)

    async function initializeAuth() {
        try {
            await sdk.auth.getCsrfCookie(PLATFORM_IDS.AI_PLATFORM)
            const response = await sdk.general.init(PLATFORM_IDS.AI_PLATFORM)
            user.value = response.data.user ?? null
        } catch (error) {
            user.value = null
        } finally {
            isInitialized.value = true
        }
    }

    async function login(credentials: { email: string; password: string }) {
        await sdk.auth.login({
            ...credentials,
            platform_id: PLATFORM_IDS.AI_PLATFORM,
            platform_type: 1 // TYPE_WEB
        })
        // After login, fetch user data via init
        const response = await sdk.general.init(PLATFORM_IDS.AI_PLATFORM)
        user.value = response.data.user ?? null
    }

    async function loginViaGoogle() {
        const response = await sdk.auth.loginViaGoogle(PLATFORM_IDS.AI_PLATFORM)
        return response
    }

    async function logout() {
        await sdk.auth.logout()
        user.value = null
    }

    function hasRole(role: string) {
        return user.value?.roles?.some((r: any) => r.name === role) || false
    }

    function hasAnyRole(roles: string[]) {
        return roles.some(role => hasRole(role))
    }

    return {
        user,
        isAuthenticated,
        isInitialized,
        initializeAuth,
        login,
        loginViaGoogle,
        logout,
        hasRole,
        hasAnyRole
    }
})
